import { create } from 'zustand';
import { useTimezoneStore } from 'infrastructure/useTimeZoneStore';

const getInitialState = () => {
  const timezoneStore = useTimezoneStore.getState();

  return {
    initiateRealPlay: false,
    realPlayAssignData: {
      roleplay: {},
      value: 0,
      personas_ai: [],
      attitudes: [],
      deadline: timezoneStore.formatDate(timezoneStore.getCurrentUserMidnight()),
      users: [],
      context: { industry: '', title: '', customer_situation: '' },
    },
    realPlayRecording: {},
    realPlayReviewToQualified: {},
    realPlayTranscription: [],
    isLoadingVideoTemp: false,
    publicAccess: false,
  };
};

export const useRealplayStore = create((set, get) => ({
  ...getInitialState(),
  //assign real play
  openModal: () => set(() => ({ initiateRealPlay: true })),
  closeModal: () => set(() => ({ initiateRealPlay: false })),
  setRolePlaySelect: (newList = {}) =>
    set((state) => ({
      realPlayAssignData: {
        ...state.realPlayAssignData,
        roleplay: newList,
      },
    })),
  setPersonasAI: (list = []) =>
    set((state) => ({
      realPlayAssignData: {
        ...state.realPlayAssignData,
        personas_ai: list,
      },
    })),
  setAttitudes: (list = []) =>
    set((state) => ({
      realPlayAssignData: {
        ...state.realPlayAssignData,
        attitudes: list,
      },
    })),
  setDeadLine: (query) =>
    set((state) => ({
      realPlayAssignData: { ...state.realPlayAssignData, deadline: query },
    })),
  refreshDeadlineWithTimezone: () => {
    const timezoneStore = useTimezoneStore.getState();
    set((state) => ({
      realPlayAssignData: {
        ...state.realPlayAssignData,
        deadline: timezoneStore.formatDate(new Date()),
      },
    }));
  },
  updateScenarioSelection: (uuid, isSelected) =>
    set((state) => ({
      realPlayAssignData: {
        ...state.realPlayAssignData,
        roleplay: {
          ...state.realPlayAssignData.roleplay,
          scenarios: (state.realPlayAssignData.roleplay.scenarios ?? []).map((sce) =>
            sce.uuid === uuid ? { ...sce, isSelected } : sce
          ),
        },
      },
    })),
  updateAttitudesSelection: (uuid) =>
    set((state) => ({
      realPlayAssignData: {
        ...state.realPlayAssignData,
        attitudes: (state.realPlayAssignData.attitudes ?? []).map((att) => ({
          ...att,
          isSelected: att?.uuid === uuid,
        })),
      },
    })),
  updatePersonaSelection: (uuid) =>
    set((state) => ({
      realPlayAssignData: {
        ...state.realPlayAssignData,
        personas_ai: (state.realPlayAssignData.personas_ai ?? []).map((persona) => ({
          ...persona,
          isSelected: persona?.uuid === uuid,
        })),
      },
    })),
  updateUsersSelection: (newUsers) =>
    set((state) => ({
      realPlayAssignData: {
        ...state.realPlayAssignData,
        users: newUsers,
      },
    })),
  updateIndustrySelector: (newValue, description) =>
    set((state) => ({
      realPlayAssignData: {
        ...state.realPlayAssignData,
        context: {
          ...state.realPlayAssignData.context,
          industry: newValue,
          customer_situation: description,
        },
      },
    })),
  updateTitleContext: (newTitle) =>
    set((state) => ({
      realPlayAssignData: {
        ...state.realPlayAssignData,
        context: {
          ...state.realPlayAssignData.context,
          title: newTitle,
        },
      },
    })),
  updateCustomerSituation: (text) =>
    set((state) => ({
      realPlayAssignData: {
        ...state.realPlayAssignData,
        context: {
          ...state.realPlayAssignData.context,
          customer_situation: text,
        },
      },
    })),
  //Record real play in progress(record interaction)
  setRealPlayRecording: (realPlayRecording = {}) => set(() => ({ realPlayRecording })),
  setScenarioWithSelector: (newList = {}) =>
    set((state) => ({
      realPlayRecording: {
        ...state.realPlayRecording,
        scenarioSelected: newList,
      },
    })),
  setIsRecordingScenario: (newValue) =>
    set((state) => ({
      realPlayRecording: {
        ...state.realPlayRecording,
        scenarioSelected: {
          ...state.realPlayRecording.scenarioSelected,
          isRecording: newValue,
        },
      },
    })),
  setTempVideoUrl: (url) =>
    set((state) => ({
      realPlayRecording: {
        ...state.realPlayRecording,
        scenarioSelected: {
          ...state.realPlayRecording.scenarioSelected,
          tempVideoUrl: url,
        },
      },
    })),
  setSessionInfoTemp: (newSession = {}) =>
    set((state) => ({
      realPlayRecording: {
        ...state.realPlayRecording,
        sessionInfoTemp: newSession,
      },
    })),
  updateSessionStatus: (newStatus) =>
    set((state) => ({
      realPlayRecording: {
        ...state.realPlayRecording,
        sessionInfoTemp: {
          ...state.realPlayRecording.sessionInfoTemp,
          status: newStatus,
        },
      },
    })),
  setIsLoadingVideoTemp: (value) => set({ isLoadingVideoTemp: value }),
  setRealPlayReviewToQualified: (realPlayReviewToQualified = {}) =>
    set(() => ({ realPlayReviewToQualified })),
  setIsEditing: (newValue) =>
    set((state) => ({
      realPlayReviewToQualified: {
        ...state.realPlayReviewToQualified,
        isEditing: newValue,
      },
    })),
  setFeedback: (value) =>
    set((state) => ({
      realPlayReviewToQualified: {
        ...state.realPlayReviewToQualified,
        feedback: value,
      },
    })),
  setRatingCriteria: (newRating, Uuid) =>
    set((state) => {
      const criteriaList = [...(state.realPlayReviewToQualified.rating_criteria ?? [])];
      const index = criteriaList.findIndex((c) => c.uuid === Uuid);

      if (index !== -1) {
        criteriaList[index] = {
          ...criteriaList[index],
          rating: newRating ?? 0,
        };
      }

      return {
        realPlayReviewToQualified: {
          ...state.realPlayReviewToQualified,
          rating_criteria: criteriaList,
        },
      };
    }),
  setDiscardChanges: () =>
    set((state) => ({
      realPlayReviewToQualified: {
        ...state.realPlayReviewToQualified,
        feedback: state.realPlayReviewToQualified.feedbackTemp,
        rating_criteria: state.realPlayReviewToQualified.rating_criteria_temp,
      },
    })),
  setRealPlayTranscription: (realPlayTranscription = []) => set(() => ({ realPlayTranscription })),
  setPublicAccess: (value) => set({ publicAccess: value }),
  reset: () => {
    set(getInitialState());
  },
}));

let unsubscribe;
export const initializeRealplayStoreSubscription = () => {
  if (unsubscribe) unsubscribe();

  unsubscribe = useTimezoneStore.subscribe(() => {
    useRealplayStore.getState().refreshDeadlineWithTimezone();
  });
};

export const cleanupRealplayStoreSubscription = () => {
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
};
